import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const muted = { color: "var(--fb-text-muted)" };

export default function AddressValidationSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api.get("/address-validation/config")
      .then((res) => { if (mounted.current) setConfig(res.data); })
      .catch((e) => { if (mounted.current) setError(formatApiError(e)); })
      .finally(() => { if (mounted.current) setLoading(false); });
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  return (
    <div className="fb-card p-4 sm:p-6" data-testid="address-validation-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
          <MapPin className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Address Validation</h3>
          <p className="text-sm" style={muted}>Address checking is managed by the server.</p>
        </div>
      </div>

      {error && (
        <div role="alert" className="fb-status-error text-sm p-3 rounded-lg mb-4" data-testid="address-validation-error">
          {error}{" "}
          {!config && <button type="button" className="underline font-semibold" onClick={load} data-testid="address-validation-retry">Retry</button>}
        </div>
      )}

      {loading ? (
        <div className="text-sm py-4" style={muted} data-testid="address-validation-loading">Loading settings…</div>
      ) : !config ? null : (
        <div className="space-y-2 text-sm" data-testid="address-validation-readonly">
          <p data-testid="address-validation-usps">USPS: automatic when an address field is left.</p>
          <p data-testid="address-validation-google">
            Google: {config.google_active ? "available through the explicit Check address button." : "not available right now."}
          </p>
          <p style={muted}>Provider choice, limits and credentials are backend-managed configuration and are not editable here.</p>
          {typeof config.daily_remaining === "number" && (
            <p className="text-xs" style={muted} data-testid="address-validation-remaining">{config.daily_remaining} Google checks remaining today.</p>
          )}
        </div>
      )}
    </div>
  );
}
